import * as yup from 'yup'
import { CSSProperties, useEffect, useState, useMemo } from 'react'
import { IOptionsProps } from '../../../../../@types/reactSelect/IOption'
import { useEditOnboarding } from '../../../../../hooks/useEditOnboarding'
import { farmService } from '../../../../../services/farmService'
import { areaService } from '../../../../../services/areaService'
import { calculateHectares } from '../../../../../utils/geolib/calculateHectares'
import { FooterEditOnboarding } from '../../../../EditOnboarding/Footer'
import { ICoords } from '../../../../../@types/GoogleMaps/ICoord'
import { AlertModal } from '../../../../Modal/Alert/Index'
import { SimpleButtonClose } from '../../../../Modal/Contents/SimpleButtonClose/Index'
import { TextModal } from '../../../../Modal/Contents/TextModal/Index'
import { TitleModal } from '../../../../Modal/Contents/TitleModal/Index'
import { ButtonRedirect } from '../../../../Modal/Error/ButtonRedirect/Index'
import { moveCharLeft } from '../../../../../utils/arrays/moveChar'
import { microAreaService } from '../../../../../services/microAreaService'
import { MicroAreaFormV2, MicroAreaFormValueV2, FieldErrorsV2 } from '../../../../FormsV2'
import { useTranslation } from 'react-i18next'
import { DrawOnMapSlotV2 } from '../../../../OnboardingV2/DrawOnMapSlotV2'
import '../../../styles.css'

function buildMicroAreaFormValue(
  defaultValue: any,
  farms?: IOptionsProps,
  areas?: IOptionsProps,
  reasons?: IOptionsProps,
): MicroAreaFormValueV2 {
  const farm = farms?.find((option) =>
    option.value === defaultValue?.farm_id ||
    option.info?.initials === defaultValue?.farm_initials
  ) || null
  const area = areas?.find((option) =>
    option.value === defaultValue?.area_id ||
    option.info?.initials === defaultValue?.area_initials
  ) || null
  const reason = reasons?.find((option) => option.value === defaultValue?.reason_id) || null

  return {
    id: defaultValue?.id,
    uuid: defaultValue?.uuid,
    farm,
    area,
    initials: defaultValue?.initials || '',
    acreage: defaultValue?.acreage || '',
    reason,
    map_coords: defaultValue?.map_coords || [],
  }
}

function toFieldErrors(error: yup.ValidationError) {
  return error.inner.reduce((errors, currentError) => {
    if (currentError.path) errors[currentError.path] = currentError.message
    return errors
  }, {} as FieldErrorsV2)
}

export function MicroArea() {
  const { t } = useTranslation()
  const { setOptionForm, defaultValue } = useEditOnboarding()
  const [reasonOptions, setReasonOptions] = useState<IOptionsProps>()
  const [listFarms, setListFarms] = useState<IOptionsProps>()
  const [listAreas, setListAreas] = useState<IOptionsProps>()
  const [currentFarm, setCurrentFarm] = useState<any>()
  const [currentArea, setCurrentArea] = useState<any>()
  const [currentInitials, setCurrentInitials] = useState<string>('')
  const isNew = !defaultValue
  const [staticMicroAreas, setStaticMicroAreas] = useState<ICoords[]>([])
  const [acreageArea, setAcreageArea] = useState('')
  const [msgError, setMsgError] = useState<string>('')
  const [lenghtMicroArea, setLenghtMicroArea] = useState(0)
  const [submiting, setSubmiting] = useState(false);
  const [microAreaV2, setMicroAreaV2] = useState<MicroAreaFormValueV2>(() =>
    buildMicroAreaFormValue(defaultValue, listFarms, listAreas, reasonOptions)
  )
  const [errorsV2, setErrorsV2] = useState<FieldErrorsV2>({})

  const microAreaSchemaV2 = yup.object().shape({
    farm: yup.object().nullable().required(t('internalValidation.farmRequired')),
    area: yup.object().nullable().required(t('internalValidation.areaRequired')),
    initials: yup
      .string()
      .matches(/^\S+(\s+\S+)*$/, t('internalValidation.invalidInitials'))
      .required(t('internalValidation.initialsRequired')),
    acreage: yup
      .string()
      .test('value', t('internalValidation.required'), () => Number(microAreaV2.acreage?.replace(',', '.') || 0) !== 0)
      .required(t('internalValidation.required')),
    map_coords: yup.array().min(1, t('internalValidation.mapPaddockRequired')),
  })

  const contentStyleAlert = {
    width: '335px',
    height: '237px',
    borderRadius: '20px',
    padding: '20px',
  } as CSSProperties

  const submitMicroAreaV2 = async () => {
    if (submiting) return

    try {
      await microAreaSchemaV2.validate(microAreaV2, { abortEarly: false })
      setErrorsV2({})
    } catch (error) {
      if (error instanceof yup.ValidationError) setErrorsV2(toFieldErrors(error))
      return
    }

    setSubmiting(true)
    const acreageFix = microAreaV2.acreage?.replace(',', '.')

    if (isNew) {
      const formNewMicroArea = [
        {
          initials: microAreaV2.initials,
          acreage: moveCharLeft(acreageFix, acreageFix.charAt(2)),
          area_id: microAreaV2.area?.value,
          map_coords: microAreaV2.map_coords,
          micro_area_reason_id: microAreaV2.reason?.value,
        },
      ]

      microAreaService
        .createMicroAreas(formNewMicroArea)
        .then(() => setOptionForm(undefined))
        .catch((error) => setMsgError(error.response.data.msg))
        .finally(() => setSubmiting(false))
    } else {
      const formPathMicroArea = [
        {
          micro_area_id: defaultValue?.id,
          initials: microAreaV2.initials,
          acreage: moveCharLeft(acreageFix, acreageFix.charAt(2)),
        },
      ]

      microAreaService
        .updateMicroAreas(formPathMicroArea)
        .then(() => setOptionForm(undefined))
        .catch((error) => setMsgError(error.response.data.msg))
        .finally(() => setSubmiting(false))
    }
  }

  function updateMicroAreaV2(value: MicroAreaFormValueV2) {
    setMicroAreaV2(value)
    if (value.farm?.value !== microAreaV2.farm?.value) {
      setCurrentFarm(value.farm?.info)
    }
    if (value.area?.value !== microAreaV2.area?.value) {
      setCurrentArea(value.area?.info)
    }
  }

  function updateMicroAreaMapCoords(map_coords: ICoords) {
    const acreage = map_coords.length >= 3 ? calculateHectares(map_coords) : microAreaV2.acreage
    setMicroAreaV2((current) => ({ ...current, map_coords, acreage }))
  }

  useEffect(() => {
    microAreaService.getMicroAreasReasons().then((data) => {
      setReasonOptions(
        data.map((option: any) => ({
          value: option.id,
          label: option.description,
        })),
      )
    })

    farmService.getFarmsByUser().then((data) => {
      setListFarms(
        data.map((farm: any) => ({
          label: farm.name,
          value: farm.id,
          info: farm,
        })),
      )
    })

    areaService.getAreasByUser().then((data) => {
      setListAreas(
        data.map((area: any) => ({
          label: area.name,
          value: area.id,
          info: area,
        })),
      )
    })
  }, [])

  useEffect(() => {
    if (currentFarm) {
      setCurrentInitials(`${currentFarm?.initials}`)
    }
  }, [currentFarm])

  useEffect(() => {
    setAcreageArea(defaultValue?.remaining_area)
  }, [defaultValue])

  const remainingAreaV2 = useMemo(() => {
    if (!(currentArea || defaultValue?.area_id)) return ''

    const areaTotal = Number(acreageArea) || Number(currentArea?.acreage) || Number(defaultValue?.remaining_area) || 0
    const currentAcreageNumber = Number(String(microAreaV2.acreage || '0').replace(',', '.')) || 0

    return (areaTotal - currentAcreageNumber).toFixed(2)
  }, [acreageArea, currentArea, defaultValue?.area_id, defaultValue?.remaining_area, microAreaV2.acreage])

  const deleteMicroArea = () => {
    microAreaService
      .deleteMicroArea(defaultValue.id)
      .then(() => {
        setOptionForm(undefined)
      })
      .catch((err) => setMsgError(err.response.data.msg))
  }

  useEffect(() => {
    if (currentArea) {
      setCurrentInitials(`${currentFarm?.initials}-${currentArea?.initials}`)

      // Buscar microáreas existentes para o mapa
      microAreaService
        .getMicroAreasByAreaId(currentArea.id)
        .then((data) => {
          const otherMicroAreas = data.filter((micro) => micro.id !== defaultValue?.id)
          setStaticMicroAreas(otherMicroAreas.map((micro) => micro.map_coords))
        })

      // Buscar dados para cálculo de área restante
      microAreaService
        .getMicroAreasByFarmAndArea(currentFarm?.id, currentArea?.id)
        .then((data) => {
          // Excluir a microárea atual quando estivermos editando, para que o
          // cálculo da área disponível não subtraia a mesma área duas vezes.
          const otherMicroAreas = data.filter((micro: any) => micro.id !== defaultValue?.id)
          const acregeUsed = otherMicroAreas.reduce(
            (sum: number, current: any) => sum + current.acreage,
            0,
          )
          setLenghtMicroArea(otherMicroAreas.length)
          setAcreageArea((currentArea?.acreage - acregeUsed).toFixed(2))
        })
    }
  }, [currentArea, currentFarm])

  useEffect(() => {
    if (!isNew && defaultValue && listFarms && listAreas && reasonOptions) {
      const nextValue = buildMicroAreaFormValue(defaultValue, listFarms, listAreas, reasonOptions)
      setMicroAreaV2(nextValue)
      setCurrentFarm(nextValue.farm?.info)
      setCurrentArea(nextValue.area?.info)
    }
  }, [defaultValue, isNew, listFarms, listAreas, reasonOptions])

  useEffect(() => {
    const nextInitials = isNew ? `${(lenghtMicroArea + 1).toString().padStart(3, '0')}` : defaultValue?.initials || ''
    setMicroAreaV2((current) => current.initials === nextInitials ? current : { ...current, initials: nextInitials })
  }, [defaultValue?.initials, isNew, lenghtMicroArea])

  const filteredAreas = listAreas
    ? listAreas.filter((area: any) => !currentFarm || area?.info?.farm?.id === currentFarm?.id)
    : []
  const initialsPrefix = isNew
    ? currentInitials
      ? `${currentInitials}-`
      : ''
    : `${defaultValue?.farm_initials}-${defaultValue?.area_initials}-`

  return (
    <>
      <AlertModal visible={!!msgError} contentStyle={contentStyleAlert}>
        <section className="EditAreas_modalalert-container">
          <SimpleButtonClose onClick={() => setMsgError('')} />
          <main className="EditAreas_modalalert-content">
            <TitleModal>{t("managementUi.somethingWrong")}</TitleModal>
            <TextModal>{msgError}</TextModal>
          </main>
          <ButtonRedirect onClick={() => setMsgError('')}>
            Voltar
          </ButtonRedirect>
        </section>
      </AlertModal>
      <main className="body__formEditOnboarding body__formEditOnboarding--property-v2">
        <MicroAreaFormV2
          value={microAreaV2}
          mode={isNew ? 'create' : 'edit'}
          farms={listFarms || []}
          areas={filteredAreas}
          reasons={isNew ? reasonOptions || [] : []}
          initialsPrefix={initialsPrefix}
          availableArea={remainingAreaV2}
          errors={errorsV2}
          disabledFields={isNew ? [] : ['farm', 'area', 'acreage']}
          showActions={false}
          showCardHeader={false}
          cardTitle={isNew ? t('formCards.newPaddock') : t('formsV2.paddock')}
          cardDescription={microAreaV2.initials ? `${initialsPrefix}${microAreaV2.initials}` : undefined}
          onFarmChange={(farm) => {
            setCurrentFarm(farm?.info)
            setCurrentArea(undefined)
          }}
          onAreaChange={(area) => setCurrentArea(area?.info)}
          mapSlot={
            <div className="edit-property-v2__map-slot">
              <DrawOnMapSlotV2
                value={microAreaV2.map_coords as ICoords}
                staticsElements={staticMicroAreas}
                fitCoords={currentArea?.map_coords || microAreaV2.area?.info?.map_coords || (microAreaV2.map_coords as ICoords) || []}
                initialsLabel={`${initialsPrefix}${microAreaV2.initials || ''}`}
                editable={isNew && (!!currentArea || !!microAreaV2.area)}
                style={{ width: '100%', height: '240px' }}
                onChange={updateMicroAreaMapCoords}
              />
            </div>
          }
          onChange={updateMicroAreaV2}
        />
      </main>

      <FooterEditOnboarding
        undoFunction={() => setOptionForm(undefined)}
        saveFunction={submitMicroAreaV2}
        deleteFunction={isNew ? undefined : deleteMicroArea}
        isNew={isNew}
        disabledSubmit={submiting}
      />
    </>
  )
}
